<template>
	<div v-loading="loading" class="m-skill">
		<div class="skill-stats">
			<div class="stat">
				<div class="stat-num">{{ stats.total }}</div>
				<div class="stat-label">项技能</div>
			</div>
			<div class="stat">
				<div class="stat-num">{{ stats.skilled }}</div>
				<div class="stat-label">熟练以上</div>
			</div>
			<div class="stat">
				<div class="stat-num">{{ stats.hours }}</div>
				<div class="stat-label">累计打卡小时</div>
			</div>
		</div>

		<div class="skill-group" v-for="group in tree" :key="group.id">
			<div class="group-head">
				<i class="folder open outline icon"></i>{{ group.name }}
				<span class="group-desc" v-if="group.description">{{ group.description }}</span>
			</div>
			<div class="skill-list">
				<div class="skill-item" v-for="skill in group.children" :key="skill.id">
					<div class="skill-name">
						{{ skill.name }}
						<span class="skill-years" v-if="skill.years">{{ skill.years }} 年</span>
					</div>
					<div class="skill-dots">
						<span class="dot" v-for="n in 5" :key="n" :class="{filled: n <= skill.level}"></span>
						<span class="skill-level">{{ levelText(skill.level) }}</span>
					</div>
					<div class="skill-desc" v-if="skill.description">{{ skill.description }}</div>
				</div>
			</div>
		</div>

		<div v-if="!loading && !tree.length" class="skill-empty">技能数据还没配置</div>

		<div class="skill-logs" v-if="logs.length">
			<div class="group-head"><i class="history icon"></i>最近的学习记录</div>
			<div class="log-item" v-for="log in logs" :key="log.id">
				<span class="log-date">{{ formatDate(log.logDate || log.createTime) }}</span>
				<span class="log-content">{{ log.content }}</span>
				<span class="log-minutes" v-if="log.minutes">{{ log.minutes }} 分钟</span>
			</div>
		</div>
	</div>
</template>

<script>
	import {getSkillTree, getSkillLogs} from '@/api/wxtools'

	const LEVEL_TEXT = ['了解', '入门', '基本掌握', '熟练', '精通', '专家']

	export default {
		name: 'SkillCard',
		data() {
			return {
				loading: true,
				tree: [],
				logs: []
			}
		},
		computed: {
			stats() {
				let total = 0
				let skilled = 0
				let minutes = 0
				this.tree.forEach((group) => {
					(group.children || []).forEach((skill) => {
						total++
						if (skill.level >= 3) {
							skilled++
						}
						minutes += skill.totalMinutes || 0
					})
				})
				return {total, skilled, hours: Math.round(minutes / 60)}
			}
		},
		created() {
			Promise.all([
				getSkillTree().then((res) => {
					if (res.code === 200) {
						this.tree = res.data || []
					}
				}),
				getSkillLogs().then((res) => {
					if (res.code === 200) {
						this.logs = (res.data || []).slice(0, 8)
					}
				})
			]).catch(() => {
				this.$message.error('技能数据加载失败')
			}).finally(() => {
				this.loading = false
			})
		},
		methods: {
			levelText(level) {
				return LEVEL_TEXT[level] || LEVEL_TEXT[0]
			},
			formatDate(value) {
				return value ? String(value).slice(0, 10) : ''
			}
		}
	}
</script>

<style scoped>
	.skill-stats {
		display: flex;
		gap: 12px;
		margin-bottom: 20px;
	}

	.stat {
		flex: 1;
		text-align: center;
		padding: 14px 8px;
		border-radius: 6px;
		background: linear-gradient(135deg, rgba(64, 158, 255, .08), rgba(64, 158, 255, .03));
	}

	.stat-num {
		font-size: 24px;
		font-weight: 600;
		color: #409eff;
		line-height: 1.2;
	}

	.stat-label {
		font-size: 12px;
		color: rgba(0, 0, 0, .5);
		margin-top: 4px;
	}

	.group-head {
		font-size: 14px;
		font-weight: 500;
		color: rgba(0, 0, 0, .75);
		margin: 18px 0 10px;
		padding-bottom: 6px;
		border-bottom: 1px solid rgba(34, 36, 38, .08);
	}

	.group-head i {
		color: #409eff;
		margin-right: 4px !important;
	}

	.group-desc {
		font-size: 12px;
		font-weight: 400;
		color: rgba(0, 0, 0, .35);
		margin-left: 8px;
	}

	.skill-list {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
		gap: 10px 20px;
	}

	.skill-item {
		padding: 8px 0;
	}

	.skill-name {
		font-size: 13px;
		color: rgba(0, 0, 0, .75);
		margin-bottom: 5px;
	}

	.skill-years {
		font-size: 11px;
		color: rgba(0, 0, 0, .35);
		margin-left: 6px;
	}

	.skill-dots {
		display: flex;
		align-items: center;
	}

	.dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: rgba(0, 0, 0, .1);
		margin-right: 4px;
	}

	.dot.filled {
		background: #409eff;
	}

	.skill-level {
		font-size: 11px;
		color: rgba(0, 0, 0, .4);
		margin-left: 6px;
	}

	.skill-desc {
		font-size: 12px;
		color: rgba(0, 0, 0, .4);
		margin-top: 4px;
		line-height: 1.4;
	}

	.skill-empty {
		text-align: center;
		padding: 30px 0;
		color: rgba(0, 0, 0, .35);
		font-size: 13px;
	}

	.log-item {
		display: flex;
		align-items: baseline;
		font-size: 13px;
		padding: 6px 0;
		border-bottom: 1px dashed rgba(0, 0, 0, .05);
	}

	.log-date {
		color: rgba(0, 0, 0, .35);
		font-size: 12px;
		flex-shrink: 0;
		width: 90px;
	}

	.log-content {
		flex: 1;
		color: rgba(0, 0, 0, .65);
	}

	.log-minutes {
		font-size: 12px;
		color: #67c23a;
		flex-shrink: 0;
		margin-left: 8px;
	}
</style>
